<template>
  <button
    class="ui-button"
    :class="[`ui-button--${variant}`, `ui-button--${size}`, { 'ui-button--block': block }]"
    :type="type"
    :data-tone="tone ?? undefined"
  >
    <span v-if="$slots.icon" class="ui-button__icon">
      <slot name="icon" />
    </span>
    <span class="ui-button__label">
      <slot />
    </span>
  </button>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    variant?: "primary" | "secondary" | "ghost" | "danger" | "text";
    size?: "sm" | "md" | "lg";
    block?: boolean;
    type?: "button" | "submit" | "reset";
    tone?: "travel" | "work" | "combat" | "econ" | "shop" | "social" | null;
  }>(),
  {
    variant: "primary",
    size: "md",
    block: false,
    type: "button",
    tone: null
  }
);
</script>

<style scoped>
.ui-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1.15rem;
  border-radius: var(--radius-1);
  border: 1px solid transparent;
  font-weight: 600;
  letter-spacing: 0.01em;
  transition: transform var(--dur-1) var(--ease-out), box-shadow var(--dur-1) var(--ease-out), border-color var(--dur-1) var(--ease-out);
}

.ui-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.ui-button--primary {
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--accent) 90%, white),
    color-mix(in srgb, var(--accent) 64%, transparent)
  );
  color: var(--bg-0);
  border-color: color-mix(in srgb, var(--accent) 42%, transparent);
  box-shadow: 0 10px 20px color-mix(in srgb, var(--accent) 22%, transparent);
}

.ui-button--secondary {
  background: var(--surface-muted-bg);
  color: var(--fg-0);
  border-color: var(--surface-muted-border);
}

.ui-button--ghost {
  background: var(--surface-ghost-bg);
  color: var(--fg-0);
  border-color: color-mix(in srgb, var(--accent) 18%, transparent);
}

.ui-button--danger {
  background: color-mix(in srgb, var(--state-danger) 16%, transparent);
  color: var(--fg-0);
  border-color: color-mix(in srgb, var(--state-danger) 55%, transparent);
}

.ui-button--text {
  background: transparent;
  color: var(--fg-1);
  border-color: transparent;
  padding: 0.3rem 0.4rem;
}

.ui-button--sm {
  padding: 0.45rem 0.75rem;
  font-size: 0.85rem;
}

.ui-button--lg {
  padding: 0.85rem 1.5rem;
  font-size: 1rem;
}

.ui-button--block {
  width: 100%;
}

.ui-button:not(:disabled):hover {
  transform: translateY(-1px);
}

.ui-button__icon {
  font-size: 1rem;
  line-height: 1;
}

.ui-button[data-tone="travel"] {
  --accent: var(--accent-travel);
}

.ui-button[data-tone="work"] {
  --accent: var(--accent-work);
}

.ui-button[data-tone="combat"] {
  --accent: var(--accent-combat);
}

.ui-button[data-tone="econ"] {
  --accent: var(--accent-econ);
}

.ui-button[data-tone="shop"] {
  --accent: var(--accent-shop);
}

.ui-button[data-tone="social"] {
  --accent: var(--accent-social);
}
</style>
